import React, { useCallback, useState } from "react";

import { faPenClip } from "@fortawesome/pro-solid-svg-icons";
import { faStickyNote } from "@fortawesome/pro-solid-svg-icons/faStickyNote";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import HasNote from "@mapmycustomers/shared/types/base/HasNote";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import TextArea from "@app/component/input/TextArea";
import Lexer from "@app/component/Lexer";
import LongContentParagraph from "@app/component/typography/LongContentParagraph";
import MultilineText from "@app/component/typography/MultilineText";

import messages from "./messages";
import styles from "./NoteEditor.module.scss";

interface Props<T extends HasNote> {
  className?: string;
  entity: T;
  loading?: boolean;
  onSave?: (data: { entity: T; noteAdded?: boolean; onSuccess?: () => void }) => void;
}

const NoteEditor = <T extends HasNote>({ className, entity, loading, onSave }: Props<T>) => {
  const intl = useIntl();

  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [exitPopoverOpen, setExitPopoverOpen] = useState<boolean>(false);
  const [note, setNote] = useState<null | string>(entity.note);
  const [editing, startEditing, stopEditing] = useBoolean(!entity.note);
  const modified = entity?.note !== note;

  const resetNote = useCallback(() => {
    if (entity.note) {
      stopEditing();
    }
    setNote(entity.note);
  }, [entity.note, setNote, stopEditing]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setPopoverOpen((popoverOpen) => {
        if (popoverOpen && editing && modified) {
          setExitPopoverOpen(true);
          return popoverOpen;
        } else {
          setPopoverOpen(open);
          if (!open) {
            setExitPopoverOpen(false);
            resetNote();
          }
          return open;
        }
      });
    },
    [editing, modified, resetNote, setExitPopoverOpen, setPopoverOpen]
  );

  const handleExitPopoverOpenChange = useCallback(
    () => handleOpenChange(false),
    [handleOpenChange]
  );

  const handleCancelExit = useCallback(() => setExitPopoverOpen(false), [setExitPopoverOpen]);

  const handleCancelEdit = useCallback(() => {
    if (entity.note) {
      stopEditing();
    } else {
      setExitPopoverOpen(false);
      setPopoverOpen(false);
      resetNote();
    }
  }, [entity.note, resetNote, setExitPopoverOpen, setPopoverOpen, stopEditing]);

  const handleHidePopoverConfirm = useCallback(() => {
    setExitPopoverOpen(false);
    setPopoverOpen(false);
    resetNote();
  }, [resetNote, setExitPopoverOpen, setPopoverOpen]);

  const handleHidePopover = useCallback(
    (isUpdate: boolean) => {
      if (isUpdate) {
        stopEditing();
      } else {
        startEditing();
        setNote(null);
      }
      setPopoverOpen(false);
    },
    [setNote, setPopoverOpen, startEditing, stopEditing]
  );

  const handleSave = useCallback(() => {
    onSave?.({
      entity: { ...entity, note },
      noteAdded: !entity?.note,
      onSuccess: () => handleHidePopover(true),
    });
  }, [entity, handleHidePopover, note, onSave]);

  const handleDelete = useCallback(() => {
    onSave?.({
      entity: { ...entity, note: null },
      noteAdded: !entity?.note,
      onSuccess: () => handleHidePopover(false),
    });
  }, [entity, handleHidePopover, onSave]);

  const disabled = editing && (!modified || isEmptyString(note ?? ""));

  if (!onSave && !entity.note) {
    // Wrapping into fragment because this component can't return a plain string.
    // On the other hand it can't return `null` because that's an AgGrid recommendation
    // to return empty strings: https://www.ag-grid.com/react-hooks/#react-null
    return <>{""}</>;
  }

  return (
    <Popover
      content={
        <div className={styles.noteContainer}>
          {editing ? (
            <>
              <div className={styles.header}>
                <span className={styles.title}>{intl.formatMessage(messages.note)}</span>
                <Popconfirm
                  cancelText={intl.formatMessage(messages.no)}
                  description={intl.formatMessage(messages.leaveConfirm)}
                  okText={intl.formatMessage(messages.yes)}
                  onCancel={handleCancelExit}
                  onConfirm={handleHidePopoverConfirm}
                  onOpenChange={handleExitPopoverOpenChange}
                  open={exitPopoverOpen}
                  placement="right"
                >
                  <Button
                    icon={<FontAwesomeIcon icon={faXmark} />}
                    iconOnly
                    size="small"
                    type="control"
                  />
                </Popconfirm>
              </div>
              <TextArea
                autoFocus
                className={styles.note}
                onChange={setNote}
                placeholder={intl.formatMessage(messages.placeholder)}
                rows={4}
                value={note ?? ""}
              />
            </>
          ) : (
            <LongContentParagraph
              className={styles.paragraph}
              expandable={false}
              noMargin
              paragraphClassName={styles.note}
              rows={15}
            >
              <MultilineText wrapLinesWith={Lexer}>{entity.note ?? ""}</MultilineText>
            </LongContentParagraph>
          )}

          <div className={styles.btnContainer}>
            {editing ? (
              <>
                <Button onClick={handleCancelEdit} size="small" type="default">
                  {intl.formatMessage(messages.cancel)}
                </Button>
                <Button
                  disabled={disabled}
                  loading={loading}
                  onClick={handleSave}
                  size="small"
                  type="primary"
                >
                  {intl.formatMessage(messages.save)}
                </Button>
              </>
            ) : onSave ? (
              <>
                <Popconfirm
                  cancelText={intl.formatMessage(messages.cancel)}
                  description={intl.formatMessage(messages.deleteConfirm)}
                  okText={intl.formatMessage(messages.yes)}
                  onConfirm={handleDelete}
                  placement="bottom"
                >
                  <Button
                    danger
                    icon={
                      <Tooltip placement="top" title={intl.formatMessage(messages.delete)}>
                        <FontAwesomeIcon icon={faTrash} size="sm" />
                      </Tooltip>
                    }
                    iconOnly
                    type="text"
                  />
                </Popconfirm>
                <Button
                  icon={
                    <Tooltip placement="top" title={intl.formatMessage(messages.edit)}>
                      <FontAwesomeIcon icon={faPenClip} size="sm" />
                    </Tooltip>
                  }
                  iconOnly
                  onClick={startEditing}
                  type="text"
                />
              </>
            ) : null}
          </div>
        </div>
      }
      destroyTooltipOnHide
      onOpenChange={handleOpenChange}
      open={popoverOpen}
      placement="bottomLeft"
      trigger={!entity.note || editing ? ["click"] : ["hover"]}
    >
      <div
        className={cn(styles.container, entity.note && styles.hasNote, className)}
        onClick={stopEvents}
      >
        <FontAwesomeIcon className={styles.icon} icon={faStickyNote} />
        <span className={styles.label}>
          {entity.note
            ? intl.formatMessage(messages.viewNote)
            : intl.formatMessage(messages.addNote)}
        </span>
      </div>
    </Popover>
  );
};

export default NoteEditor;
